import { EntityType } from '@frontend/common';
import { CommonMessage } from '@frontend/lang';
import { ProductType } from '@frontend/product/types';
import { RequirementClient } from '@frontend/stock/api';
import { Requirement } from '@frontend/stock/types';
import { FormattedMessage } from 'react-intl';

import CreateModal from '../../../modal/create-modal/create-modal.component';
import { CreateRequirementModalProps, CreateRequirementWithSpot, FORM_ID, useCreateRequirementModal } from './create-requirement-modal.controller';

export const CreateRequirementModal = (props: CreateRequirementModalProps) => {
    const viewProps = useCreateRequirementModal(props);

    return (
        <CreateModal<Requirement, CreateRequirementWithSpot>
            order={['spot_id', 'product_id', 'min', 'max']}
            required={viewProps.getRequiredFields()}
            type={EntityType.REQUIREMENT}
            formId={FORM_ID}
            initialValue={{ fill_percentage: 80, fill_increment: 1 }}
            onSubmit={async (v) => {
                return await RequirementClient.postRequirement(viewProps.currentAccount, v.spot_id!, {
                    product_id: v.product_id,
                    min: v.min,
                    max: v.max,
                    fill_percentage: v.fill_percentage ?? 100
                });
            }}
            label={
                <FormattedMessage
                    id='create-requirement-modal.title'
                    defaultMessage='Create stock requirement'
                />
            }
            title={<span className='text-xl font-semibold'>{CommonMessage.FORMS.BASIC_INFORMATION}</span>}
            keyOverwrite={
                new Map([
                    ['spot_id', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                    ['product_id', CommonMessage.OBJECTS.PRODUCT.DEFAULT],
                    [
                        'min',
                        <FormattedMessage
                            key='create-requirement-modal.min-stock'
                            id='create-requirement-modal.min-stock'
                            defaultMessage='Minimum stock'
                        />
                    ],
                    [
                        'max',
                        <FormattedMessage
                            key='create-requirement-modal.max-stock'
                            id='create-requirement-modal.max-stock'
                            defaultMessage='Maximum stock'
                        />
                    ]
                ])
            }
            expectedType={
                new Map([
                    ['spot_id', 'entity'],
                    ['product_id', 'entity'],
                    ['min', 'number'],
                    ['max', 'number']
                ])
            }
            fieldProps={(values) => new Map([['product_id', { queryParams: { type: ProductType.CONSUMABLE } }]])}
            submitDisabled={viewProps.checkSubmitDisabled}
            submitButtonOverride={
                <FormattedMessage
                    id='create-requirement-modal.submit-button'
                    defaultMessage='Create stock requirement'
                />
            }
            successMessageOverwrite={
                <FormattedMessage
                    id='create-requirement-modal.success'
                    defaultMessage='Stock requirement created successfully'
                />
            }
            subForms={
                new Map([
                    [
                        <FormattedMessage
                            id='create-requirement-modal.fill-options'
                            defaultMessage='Fill options'
                            key='create-requirement-modal.fill-options'
                        />,
                        {
                            order: [['fill_increment', 'fill_percentage'], 'fill_algorithm'],
                            enumOverwrite: new Map([['fill_algorithm', 'requirement_algorithm']]),
                            fieldProps: new Map([['fill_algorithm', { disableSearch: true, isClearable: true }]])
                        }
                    ]
                ])
            }
        />
    );
};
